import { ResourceModel } from '@bryntum/schedulerpro';
import { useEffect, useRef } from 'react';
import {
  ConvertAllocationsProps,
  DeleteAllocationsProps,
} from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { ExtendedUserIdentifier, Range, SchedulerReference } from '../../types';

export const useEventListenerForScheduler = (
  schedulerRef: SchedulerReference,
  eventName: string,
  handler: (...args) => void,
): void => {
  const detacherRef = useRef<typeof Function>();

  useEffect(() => {
    if (schedulerRef.current?.instance.hasListener(eventName)) {
      detacherRef.current?.();
    }
    detacherRef.current = schedulerRef.current?.instance.on(eventName, handler);
  }, [eventName, handler, schedulerRef]);
};

export const useEventListenerForEventStore = (
  schedulerRef: SchedulerReference,
  eventName: string,
  handler: (...args) => void,
): void => {
  const detacherRef = useRef<typeof Function>();

  useEffect(() => {
    if (schedulerRef.current?.instance.eventStore.hasListener(eventName)) {
      detacherRef.current?.();
    }
    detacherRef.current = schedulerRef.current?.instance.eventStore.on(
      eventName,
      handler,
    );
  }, [eventName, handler, schedulerRef]);
};

export const useEventListenerForResourceStore = (
  schedulerRef: SchedulerReference,
  eventName: string,
  handler: (...args) => void,
): void => {
  const detacherRef = useRef<typeof Function>();

  useEffect(() => {
    if (schedulerRef.current?.instance.resourceStore.hasListener(eventName)) {
      detacherRef.current?.();
    }
    detacherRef.current = schedulerRef.current?.instance.resourceStore.on(
      eventName,
      handler,
    );
  }, [eventName, handler, schedulerRef]);
};

export interface EventAddParams {
  startDate: Date;
  endDate: Date;
  resource: ResourceModel;
}
export const useCustomEventAdd = (
  schedulerRef: SchedulerReference,
  onEventAdd: (params: EventAddParams) => void,
): void => {
  useEventListenerForScheduler(schedulerRef, 'onCustomEventAdd', onEventAdd);
};

export interface EventDeleteParams {
  event?: AllocationEventModel;
}

export const useCustomEventDelete = (
  schedulerRef: SchedulerReference,
  onEventDelete: (params: EventDeleteParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onCustomEventDelete',
    onEventDelete,
  );
};

export interface EventSplitParams {
  event: AllocationEventModel;
  splitAt: Date;
}
export const useCustomEventSplit = (
  schedulerRef: SchedulerReference,
  onEventSplit: (params: EventSplitParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onCustomEventSplit',
    onEventSplit,
  );
};

export enum ResizeEnd {
  StartDate = 'startDate',
  EndDate = 'endDate',
}
interface ResizeAtStart {
  resizedAt: ResizeEnd.StartDate;
  newStartDate: Date;
  oldStartDate: Date;
}
interface ResizeAtEnd {
  resizedAt: ResizeEnd.EndDate;
  newEndDate: Date;
  oldEndDate: Date;
}
type ResizeParam = ResizeAtStart | ResizeAtEnd;
export type EventResizeParams = ResizeParam & {
  event: AllocationEventModel;
};
export const useCustomEventResize = (
  schedulerRef: SchedulerReference,
  onEventResize: (params: EventResizeParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onCustomEventResize',
    onEventResize,
  );
};

export interface EventDragParams {
  event: AllocationEventModel;
  from: {
    startDate: Date;
    endDate: Date;
  };
  to: {
    startDate: Date;
    endDate: Date;
  };
}
export const useCustomEventDrag = (
  schedulerRef: SchedulerReference,
  onEventDrag: (params: EventDragParams) => void,
): void => {
  useEventListenerForScheduler(schedulerRef, 'onCustomEventDrag', onEventDrag);
};

export interface EventEditParams {
  event: AllocationEventModel;
}
export const useCustomEventEdit = (
  schedulerRef: SchedulerReference,
  onEventEdit: (params: EventDragParams) => void,
): void => {
  useEventListenerForScheduler(schedulerRef, 'onCustomEventEdit', onEventEdit);
};

export interface UserRemovedFromProjectParams {
  projectId: number;
  userResource: ExtendedUserIdentifier;
  rangeLoadedInUI?: Range;
}

export const useUserRemovedFromProject = (
  schedulerRef: SchedulerReference,
  onUserRemovedFromProject: (params: UserRemovedFromProjectParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onUserRemovedFromProject',
    onUserRemovedFromProject,
  );
};
export interface TeamRemovedFromProjectParams {
  projectId: number;
  teamId: number;
}

export const useTeamRemovedFromProject = (
  schedulerRef: SchedulerReference,
  onTeamRemovedFromProject: (params: TeamRemovedFromProjectParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onTeamRemovedFromProject',
    onTeamRemovedFromProject,
  );
};

export const useDeleteAllocations = (
  schedulerRef: SchedulerReference,
  onDeleteAllocations: (params: DeleteAllocationsProps) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onDeleteAllocations',
    onDeleteAllocations,
  );
};

export const useConvertAllocations = (
  schedulerRef: SchedulerReference,
  onConvertAllocations: (params: ConvertAllocationsProps) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onConvertAllocations',
    onConvertAllocations,
  );
};

export interface ToStartOfProjectParams {
  projectId: number;
}
export const useToStartOfProject = (
  schedulerRef: SchedulerReference,
  onToStartOfProjectParams: (params: ToStartOfProjectParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onToStartOfProject',
    onToStartOfProjectParams,
  );
};
interface ProjectRowHoverParams {
  projectId: number;
}
export const useProjectRowHover = (
  schedulerRef: SchedulerReference,
  onProjectRowHover: (params: ProjectRowHoverParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onProjectRowHover',
    onProjectRowHover,
  );
};

export interface UserAssignedToProjectParams {
  userId: number;
  projectId: number;
}
export const useUserAssignedToProject = (
  schedulerRef: SchedulerReference,
  onUserAssignedToProject: (params: UserAssignedToProjectParams) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onUserAssignedToProject',
    onUserAssignedToProject,
  );
};
export const usePresetChange = (
  schedulerRef: SchedulerReference,
  presetChangeHandler: (config) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'presetChange',
    presetChangeHandler,
  );
};
export const usePaintEvent = (
  schedulerRef: SchedulerReference,
  handler: (config) => void,
): void => {
  useEventListenerForScheduler(schedulerRef, 'paint', handler);
};

export interface ShowSourceTasksParams {
  event: AllocationEventModel;
}
export const useShowSourceTasks = (
  schedulerRef: SchedulerReference,
  handler: (params: ShowSourceTasksParams) => void,
): void => {
  useEventListenerForScheduler(schedulerRef, 'onShowSourceTasks', handler);
};

export interface AutoAllocationStateChangeParams {
  projectId: number;
  autoAllocation: boolean;
}
export const useAutoAllocationStateChange = (
  schedulerRef: SchedulerReference,
  onAutoAllocationStateChangeParams: (
    params: AutoAllocationStateChangeParams,
  ) => void,
): void => {
  useEventListenerForScheduler(
    schedulerRef,
    'onAutoAllocationStateChange',
    onAutoAllocationStateChangeParams,
  );
};
